import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, MagnifyingGlass, FolderSimple, FolderOpen, Plus, Star, DotsThree,
  PencilSimple, Trash, ArrowsLeftRight, Tag, Archive, CaretDown, CaretRight, Sparkle,
  Palette,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

// Private user workspace: WORKSPACE → COLLECTION → FOLDER → SAVED DESIGNS.
// Everything on this page is scoped server-side to the authenticated owner.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const STATUS_COLORS = {
  draft: "bg-white/10 text-zinc-300",
  review: "bg-blue-500/20 text-blue-300",
  approved: "bg-emerald-500/20 text-emerald-300",
  archived: "bg-zinc-700/40 text-zinc-500",
};

function NameDialog({ open, onOpenChange, title, initial = "", onSubmit }) {
  const [value, setValue] = useState(initial);
  useEffect(() => { if (open) setValue(initial); }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-sm" data-testid="ws-name-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">{title}</DialogTitle></DialogHeader>
        <input
          data-testid="ws-name-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && value.trim()) { onSubmit(value.trim()); onOpenChange(false); } }}
          autoFocus
          className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-amber-400/60"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="ws-name-submit" onClick={() => { if (value.trim()) { onSubmit(value.trim()); onOpenChange(false); } }} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MyWorkspace() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [tags, setTags] = useState([]);
  const [totals, setTotals] = useState({ total: 0, unfiled: 0 });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  // filter = { kind: 'all'|'favorites'|'collection'|'folder'|'tag', id?, label }
  const [filter, setFilter] = useState({ kind: "all", label: "All projects" });
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState(null); // {type, ...ctx}
  const [moveTarget, setMoveTarget] = useState(null); // project being moved
  const [moveCol, setMoveCol] = useState("");
  const [moveFolder, setMoveFolder] = useState("");

  const loadOverview = useCallback(() => {
    api.get("/workspace/overview").then((r) => {
      setCollections(r.data.collections || []);
      setTags(r.data.tags || []);
      setTotals({ total: r.data.total_projects || 0, unfiled: r.data.unfiled_projects || 0 });
    }).catch(() => {});
  }, []);

  const loadProjects = useCallback(() => {
    const params = new URLSearchParams();
    if (filter.kind === "collection") params.set("collection_id", filter.id);
    if (filter.kind === "folder") params.set("folder_id", filter.id);
    if (filter.kind === "tag") params.set("tag", filter.id);
    if (filter.kind === "favorites") params.set("favorite", "true");
    if (query.trim()) params.set("q", query.trim());
    api.get(`/workspace/projects?${params.toString()}`)
      .then((r) => setProjects(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, query]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  const act = async (fn, success) => {
    try { await fn(); if (success) toast.success(success); loadOverview(); loadProjects(); }
    catch (e) { toast.error(errMsg(e, "Something went wrong.")); }
  };

  const openProject = async (p) => {
    try {
      const r = await api.get(`/workspace/projects/${p.id}`);
      navigate("/ai-pro-studio", { state: { workspaceProject: r.data } });
    } catch (e) {
      toast.error(errMsg(e, "Couldn't open that project."));
    }
  };

  const sideBtn = (active) =>
    `w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-amber-400/15 text-amber-200 font-semibold" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`;

  return (
    <div data-testid="my-workspace" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} data-testid="workspace-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
          <FolderOpen size={20} weight="fill" className="text-blue-400" />
          <span className="font-display font-extrabold text-lg tracking-tight">My Workspace</span>
          <div className="ml-auto relative w-full max-w-xs">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="workspace-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects, text, tags…"
              className="w-full h-9 rounded-full bg-white/5 border border-white/10 pl-9 pr-3 text-sm outline-none focus:border-blue-400/50"
            />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8">
        {/* SIDEBAR */}
        <aside className="space-y-6">
          <div className="space-y-1">
            <button data-testid="ws-filter-all" onClick={() => setFilter({ kind: "all", label: "All projects" })} className={sideBtn(filter.kind === "all")}>
              <Sparkle size={15} weight="fill" /> All projects <span className="ml-auto text-xs text-zinc-500">{totals.total}</span>
            </button>
            <button data-testid="ws-filter-favorites" onClick={() => setFilter({ kind: "favorites", label: "Favorites" })} className={sideBtn(filter.kind === "favorites")}>
              <Star size={15} weight="fill" className="text-amber-300" /> Favorites
            </button>
            <button data-testid="ws-goto-brands" onClick={() => navigate("/workspace/brands")} className={sideBtn(false)}>
              <Palette size={15} weight="fill" className="text-amber-300" /> Brands &amp; Palettes
            </button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold">Collections</p>
              <button data-testid="ws-new-collection-btn" onClick={() => setDialog({ type: "new-collection" })} title="New collection" className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white"><Plus size={13} weight="bold" /></button>
            </div>
            <div className="space-y-0.5">
              {collections.filter((c) => !c.archived).map((c) => (
                <div key={c.id}>
                  <div className={`group flex items-center gap-1 rounded-lg ${filter.kind === "collection" && filter.id === c.id ? "bg-amber-400/15" : "hover:bg-white/5"}`}>
                    <button onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))} className="pl-2 py-2 text-zinc-500 hover:text-white">
                      {expanded[c.id] ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                    </button>
                    <button data-testid={`ws-collection-${c.id}`} onClick={() => setFilter({ kind: "collection", id: c.id, label: c.name })} className="flex-1 text-left py-2 text-sm truncate">
                      <FolderSimple size={14} weight="fill" className="inline mr-1.5 text-blue-400" />{c.name}
                      <span className="ml-1.5 text-xs text-zinc-600">{c.project_count}</span>
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="opacity-0 group-hover:opacity-100 h-7 w-7 mr-1 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400" data-testid={`ws-collection-menu-${c.id}`}><DotsThree size={16} weight="bold" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-[#161616] border-white/10 text-white">
                        <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => setDialog({ type: "rename-collection", id: c.id, initial: c.name })}><PencilSimple size={14} className="mr-2" /> Rename</DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => setDialog({ type: "new-folder", collectionId: c.id })}><Plus size={14} className="mr-2" /> New folder</DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => act(() => api.put(`/workspace/collections/${c.id}`, { archived: true }), "Collection archived.")}><Archive size={14} className="mr-2" /> Archive</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {expanded[c.id] && (c.folders || []).filter((f) => !f.archived).map((f) => (
                    <div key={f.id} className={`group flex items-center gap-1 rounded-lg ml-6 ${filter.kind === "folder" && filter.id === f.id ? "bg-amber-400/15" : "hover:bg-white/5"}`}>
                      <button data-testid={`ws-folder-${f.id}`} onClick={() => setFilter({ kind: "folder", id: f.id, label: `${c.name} / ${f.name}` })} className="flex-1 text-left px-3 py-1.5 text-[13px] text-zinc-400 truncate">
                        {f.name} <span className="ml-1 text-xs text-zinc-600">{f.project_count}</span>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="opacity-0 group-hover:opacity-100 h-6 w-6 mr-1 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-500"><DotsThree size={14} weight="bold" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-[#161616] border-white/10 text-white">
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => setDialog({ type: "rename-folder", id: f.id, initial: f.name })}><PencilSimple size={14} className="mr-2" /> Rename</DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => act(() => api.put(`/workspace/folders/${f.id}`, { archived: true }), "Folder archived.")}><Archive size={14} className="mr-2" /> Archive</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              ))}
              {collections.filter((c) => !c.archived).length === 0 && (
                <p className="text-xs text-zinc-600 px-3 py-2">No collections yet — create one to organize client work.</p>
              )}
            </div>
          </div>

          {tags.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2">Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <button key={t} data-testid={`ws-tag-${t}`} onClick={() => setFilter({ kind: "tag", id: t, label: `#${t}` })}
                    className={`px-2.5 py-1 rounded-full text-xs transition-colors ${filter.kind === "tag" && filter.id === t ? "bg-amber-400/20 text-amber-200" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}>
                    #{t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* PROJECTS */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <h1 className="font-display text-2xl font-black tracking-tight">{filter.label}</h1>
            <span className="text-sm text-zinc-500">{projects.length} project{projects.length === 1 ? "" : "s"}</span>
          </div>
          {loading ? (
            <p className="text-sm text-zinc-500 py-16 text-center">Loading your workspace…</p>
          ) : projects.length === 0 ? (
            <div data-testid="ws-empty" className="rounded-2xl border border-dashed border-white/15 p-12 text-center">
              <FolderOpen size={40} weight="duotone" className="mx-auto mb-3 text-zinc-700" />
              <p className="text-sm text-zinc-400 font-semibold">Nothing saved here yet</p>
              <p className="text-xs text-zinc-600 mt-1 mb-4">Generate a flyer in AI Pro Studio and hit “Save to Workspace”.</p>
              <button onClick={() => navigate("/ai-pro-studio")} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-bold transition-colors">
                <Sparkle size={16} weight="fill" /> Open AI Pro Studio
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
              {projects.map((p) => (
                <div key={p.id} data-testid={`ws-project-${p.id}`} className="group rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-white/25 transition-colors">
                  <button onClick={() => openProject(p)} className="block w-full aspect-[4/5] bg-black/50 relative">
                    {p.image_storage_path ? (
                      <img src={fileUrl(p.image_storage_path)} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700"><Sparkle size={28} weight="duotone" /></div>
                    )}
                    <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>{p.status}</span>
                  </button>
                  <div className="p-3">
                    <div className="flex items-start gap-1.5">
                      <p className="flex-1 text-[13px] font-semibold leading-tight truncate">{p.name}</p>
                      <button
                        data-testid={`ws-project-fav-${p.id}`}
                        onClick={() => act(() => api.put(`/workspace/projects/${p.id}`, { favorite: !p.favorite }))}
                        className="shrink-0"
                      >
                        <Star size={14} weight={p.favorite ? "fill" : "regular"} className={p.favorite ? "text-amber-300" : "text-zinc-600 hover:text-zinc-400"} />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`ws-project-menu-${p.id}`} className="shrink-0 text-zinc-500 hover:text-white"><DotsThree size={16} weight="bold" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#161616] border-white/10 text-white">
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => openProject(p)}><Sparkle size={14} className="mr-2" /> Open in Studio</DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => setDialog({ type: "rename-project", id: p.id, initial: p.name })}><PencilSimple size={14} className="mr-2" /> Rename</DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => { setMoveTarget(p); setMoveCol(p.collection_id || ""); setMoveFolder(p.folder_id || ""); }}><ArrowsLeftRight size={14} className="mr-2" /> Move…</DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10" onClick={() => setDialog({ type: "edit-tags", id: p.id, initial: (p.tags || []).join(", ") })}><Tag size={14} className="mr-2" /> Edit tags</DropdownMenuItem>
                          {["draft", "review", "approved", "archived"].filter((s) => s !== p.status).map((s) => (
                            <DropdownMenuItem key={s} className="cursor-pointer focus:bg-white/10" onClick={() => act(() => api.put(`/workspace/projects/${p.id}`, { status: s }))}>
                              Mark as {s}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem className="cursor-pointer focus:bg-white/10 text-red-400" onClick={() => act(() => api.delete(`/workspace/projects/${p.id}`), "Project deleted.")}><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {(p.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {p.tags.slice(0, 3).map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-white/5 text-[9px] text-zinc-500">#{t}</span>)}
                        {p.tags.length > 3 && <span className="text-[9px] text-zinc-600">+{p.tags.length - 3}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Dialogs */}
      <NameDialog
        open={dialog?.type === "new-collection"}
        onOpenChange={(o) => !o && setDialog(null)}
        title="New collection"
        onSubmit={(name) => act(() => api.post("/workspace/collections", { name }), "Collection created.")}
      />
      <NameDialog
        open={dialog?.type === "rename-collection"}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Rename collection"
        initial={dialog?.initial}
        onSubmit={(name) => act(() => api.put(`/workspace/collections/${dialog.id}`, { name }))}
      />
      <NameDialog
        open={dialog?.type === "new-folder"}
        onOpenChange={(o) => !o && setDialog(null)}
        title="New folder"
        onSubmit={(name) => act(() => api.post("/workspace/folders", { collection_id: dialog.collectionId, name }), "Folder created.")}
      />
      <NameDialog
        open={dialog?.type === "rename-folder"}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Rename folder"
        initial={dialog?.initial}
        onSubmit={(name) => act(() => api.put(`/workspace/folders/${dialog.id}`, { name }))}
      />
      <NameDialog
        open={dialog?.type === "rename-project"}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Rename project"
        initial={dialog?.initial}
        onSubmit={(name) => act(() => api.put(`/workspace/projects/${dialog.id}`, { name }))}
      />
      <NameDialog
        open={dialog?.type === "edit-tags"}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Edit tags (comma separated)"
        initial={dialog?.initial}
        onSubmit={(v) => act(() => api.put(`/workspace/projects/${dialog.id}`, { tags: v.split(",").map((t) => t.trim()).filter(Boolean) }), "Tags updated.")}
      />

      {/* Move dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-sm" data-testid="ws-move-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">Move “{moveTarget?.name}”</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select data-testid="ws-move-collection" value={moveCol} onChange={(e) => { setMoveCol(e.target.value); setMoveFolder(""); }} className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none">
              <option value="">Unfiled</option>
              {collections.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {moveCol && (
              <select data-testid="ws-move-folder" value={moveFolder} onChange={(e) => setMoveFolder(e.target.value)} className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none">
                <option value="">No folder</option>
                {(collections.find((c) => c.id === moveCol)?.folders || []).filter((f) => !f.archived).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMoveTarget(null)} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
            <Button data-testid="ws-move-submit" onClick={() => { const p = moveTarget; setMoveTarget(null); act(() => api.put(`/workspace/projects/${p.id}`, { collection_id: moveCol || "", folder_id: moveFolder || "" }), "Project moved."); }} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
